import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

const MOBILE_BREAKPOINT = 768;

/**
 * Reusable tooltip that positions content near an anchor element.
 * Used by ProgressTooltip and other tooltips. Renders with same visual style (dark bg, arrow).
 *
 * @param {Object} props
 * @param {React.RefObject} [props.anchorRef] - Ref to the anchor element (omit when using position/arrowLeft)
 * @param {React.ReactNode} props.children - Tooltip content
 * @param {boolean} props.visible - Whether tooltip is shown
 * @param {'top'|'bottom'} props.placement - Arrow and preferred position (top = tooltip below anchor, bottom = tooltip above anchor)
 * @param {string} [props.className] - Additional class for the tooltip wrapper
 * @param {boolean} [props.portal] - If true, render into document.body (e.g. for mobile or overflow)
 * @param {{ top: number, left: number }} [props.position] - Override: position in px (use with positionType)
 * @param {number} [props.arrowLeft] - Override: arrow position in px from left
 * @param {'absolute'|'fixed'} [props.positionType] - When using position override (default 'absolute')
 */
function Tooltip({ anchorRef, children, visible, placement = 'top', className = '', portal = false, position: positionOverride, arrowLeft: arrowLeftOverride, positionType = 'absolute' }) {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowLeft, setArrowLeft] = useState(20);
  const [isMobile, setIsMobile] = useState(false);
  const usePositionOverride = positionOverride != null;

  useLayoutEffect(() => {
    const checkMobile = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useLayoutEffect(() => {
    if (usePositionOverride || !visible || !anchorRef?.current || !tooltipRef.current) return;

    const anchor = anchorRef.current;
    const tooltipEl = tooltipRef.current;
    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width || 200;
    const tooltipHeight = tooltipRect.height || 40;
    const gap = 8;

    const anchorCenterX = anchorRect.left + anchorRect.width / 2;
    let left = anchorCenterX - tooltipWidth / 2;
    const minLeft = 12;
    const maxLeft = (typeof window !== 'undefined' ? window.innerWidth : 0) - tooltipWidth - 12;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    let top;
    const arrowPointsDown = placement === 'top'; // tooltip below anchor -> arrow on top of tooltip points up
    if (arrowPointsDown) {
      top = anchorRect.bottom + gap;
    } else {
      top = anchorRect.top - tooltipHeight - gap;
    }

    const arrowPositionRelative = anchorCenterX - left;
    const arrowLeftClamped = Math.max(12, Math.min(arrowPositionRelative, tooltipWidth - 12));

    if (portal || isMobile) {
      setPosition({ top, left });
    } else {
      const container = anchor.closest('.task-list-container') || document.body;
      const containerRect = container.getBoundingClientRect();
      const relativeTop = top - containerRect.top + (container === document.body ? 0 : container.scrollTop);
      const relativeLeft = left - containerRect.left + (container === document.body ? 0 : container.scrollLeft);
      setPosition({ top: relativeTop, left: relativeLeft });
    }
    setArrowLeft(arrowLeftClamped);
  }, [usePositionOverride, visible, placement, anchorRef, children, isMobile, portal]);

  if (!visible || !children) return null;

  const resolvedPosition = usePositionOverride ? positionOverride : position;
  const resolvedArrowLeft = usePositionOverride ? (arrowLeftOverride ?? 20) : arrowLeft;
  const computedUsePortal = portal || isMobile;
  const style = usePositionOverride
    ? { position: positionType, top: resolvedPosition.top, left: resolvedPosition.left }
    : computedUsePortal
      ? { position: 'fixed', top: resolvedPosition.top, left: resolvedPosition.left }
      : { position: 'absolute', top: resolvedPosition.top, left: resolvedPosition.left };

  const tooltipContent = (
    <div
      ref={usePositionOverride ? undefined : tooltipRef}
      className={`tooltip ${className}`}
      style={style}
      role="tooltip"
    >
      <div className="tooltip-content">
        {children}
      </div>
      <div
        className={`tooltip-arrow ${placement === 'top' ? 'tooltip-arrow-top' : 'tooltip-arrow-bottom'}`}
        style={{ left: `${resolvedArrowLeft}px` }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12">
          {placement === 'top' ? (
            <path d="M6 0 L0 12 L12 12 Z" fill="currentColor" />
          ) : (
            <path d="M6 12 L0 0 L12 0 Z" fill="currentColor" />
          )}
        </svg>
      </div>
    </div>
  );

  if (computedUsePortal && typeof document !== 'undefined' && document.body) {
    return createPortal(tooltipContent, document.body);
  }
  return tooltipContent;
}

export default Tooltip;
